
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const EcommerceAnalytics: React.FC = () => {
  const [dateRange, setDateRange] = useState('30d');
  
  // Mock data for the charts
  const conversionData = [
    { name: 'Jan', shopify: 65, woocommerce: 42, bigcommerce: 23 },
    { name: 'Feb', shopify: 78, woocommerce: 51, bigcommerce: 35 },
    { name: 'Mar', shopify: 92, woocommerce: 63, bigcommerce: 41 },
    { name: 'Apr', shopify: 105, woocommerce: 72, bigcommerce: 49 },
    { name: 'May', shopify: 120, woocommerce: 85, bigcommerce: 58 },
    { name: 'Jun', shopify: 135, woocommerce: 91, bigcommerce: 67 },
  ];
  
  const revenueData = [
    { name: 'Jan', shopify: 5200, woocommerce: 3100, bigcommerce: 1800 },
    { name: 'Feb', shopify: 6100, woocommerce: 3800, bigcommerce: 2500 },
    { name: 'Mar', shopify: 7400, woocommerce: 4500, bigcommerce: 3100 },
    { name: 'Apr', shopify: 8300, woocommerce: 5200, bigcommerce: 3700 },
    { name: 'May', shopify: 9500, woocommerce: 6100, bigcommerce: 4300 },
    { name: 'Jun', shopify: 10800, woocommerce: 6800, bigcommerce: 5100 },
  ];
  
  const platformData = [
    { name: 'Shopify', value: 55 },
    { name: 'WooCommerce', value: 30 },
    { name: 'BigCommerce', value: 10 },
    { name: 'Other', value: 5 },
  ];
  
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];
  
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div>
          <CardTitle>E-commerce Analytics</CardTitle>
          <CardDescription>
            Performance metrics for your e-commerce referral campaigns
          </CardDescription>
        </div>
        <Select value={dateRange} onValueChange={setDateRange}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Select range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
            <SelectItem value="90d">Last 90 days</SelectItem>
            <SelectItem value="6m">Last 6 months</SelectItem>
            <SelectItem value="1y">Last year</SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="conversions">
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="conversions">Conversions</TabsTrigger>
            <TabsTrigger value="revenue">Revenue</TabsTrigger>
            <TabsTrigger value="platform-breakdown">Platform Breakdown</TabsTrigger>
            <TabsTrigger value="rewards">Rewards Distribution</TabsTrigger>
          </TabsList>
          
          <TabsContent value="conversions" className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={conversionData}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="shopify" name="Shopify" fill="#0088FE" />
                <Bar dataKey="woocommerce" name="WooCommerce" fill="#00C49F" />
                <Bar dataKey="bigcommerce" name="BigCommerce" fill="#FFBB28" />
              </BarChart>
            </ResponsiveContainer>
          </TabsContent>
          
          <TabsContent value="revenue" className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={revenueData}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip formatter={(value) => [`$${value}`, 'Revenue']} />
                <Legend />
                <Line type="monotone" dataKey="shopify" name="Shopify" stroke="#0088FE" activeDot={{ r: 8 }} />
                <Line type="monotone" dataKey="woocommerce" name="WooCommerce" stroke="#00C49F" />
                <Line type="monotone" dataKey="bigcommerce" name="BigCommerce" stroke="#FFBB28" />
              </LineChart>
            </ResponsiveContainer>
          </TabsContent>
          
          <TabsContent value="platform-breakdown" className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={platformData}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {platformData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value}%`, 'Percentage']} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </TabsContent>
          
          <TabsContent value="rewards" className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={[
                  { name: 'Fixed Discount', value: 45 },
                  { name: 'Percentage Off', value: 30 },
                  { name: 'Free Shipping', value: 15 },
                  { name: 'Store Credit', value: 10 },
                ]}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="value" name="Reward Distribution" fill="#8884d8">
                  <Cell fill="#0088FE" />
                  <Cell fill="#00C49F" />
                  <Cell fill="#FFBB28" />
                  <Cell fill="#FF8042" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default EcommerceAnalytics;
